import apiClient from "@/lib/api/api-client";
import type { LiveCatalogResponse } from "@/lib/api/live-catalog";
import type { ExpectedDailyVolume } from "@/lib/api/login-user";

export type ApiPlatform = "glofox";
export type RequesterType = "customer" | "partner";
export type AccessRequestStatus = "pending" | "approved" | "rejected";
export type AccessScope = "namespaces" | "locations";
/** Key environment for integrator access (Task 5+). Optional on legacy rows. */
export type KeyEnvironment = "live" | "sandbox";
/**
 * How the access request was created (AES-359+). Optional on legacy rows.
 * Align enum values with AES-359 before merge; do not invent extra origins.
 */
export type AccessRequestOrigin = "portal" | "imported_from_glofox";

export interface AccessRequest {
  id: string;
  portal_user_id: string;
  integrator_name?: string;
  api_platform: ApiPlatform;
  requester_type: RequesterType;
  intended_use_case: string;
  /** Present when Task 5+ persists environment; omitted on legacy requests. */
  environment?: KeyEnvironment | null;
  /**
   * Present when AES-359+ returns import metadata. Badge when
   * `imported_from_glofox`.
   */
  origin?: AccessRequestOrigin | null;
  /**
   * Existing Glofox integrator id when the request was imported or credentials
   * already issued (AES-359+). Optional on legacy rows.
   */
  integrator_id?: string | null;
  /** Live fulfillment. `revoked` hides credentials and scope editing. */
  fulfillment?: "provisioned" | "revoked" | null;
  /**
   * Live API key state. `issued` and `failed` drive the credentials card.
   * Omitted when credentials were never requested. Sandbox status stays on
   * the workspace.
   */
  credentials_status?: "none" | "pending" | "issued" | "failed" | null;
  access_scope?: AccessScope | null;
  location_ids: string[];
  location_names?: string[];
  namespaces: string[];
  expected_daily_volume: ExpectedDailyVolume | null;
  integrations: string[];
  status: AccessRequestStatus;
  /** Applicant-visible reason when status is rejected. */
  rejection_reason?: string | null;
  /** Applicant response to the previous rejection. */
  contestation_message?: string | null;
  /** Time when the applicant contested the rejection. */
  contested_at?: string | null;
  /** True only for a rejected request that has not been contested. */
  can_contest?: boolean;
  created_at: string;
  updated_at: string;
  /** Newest-first persisted Request activity. Omitted on older API responses. */
  activity?: AccessRequestActivity[];
}

export interface AccessRequestActivity {
  type: string;
  created_at: string;
}

export interface CreateAccessRequestPayload {
  api_platform: ApiPlatform;
  /** Legacy Request Access (Slice 6 off). Optional when Slice 6 uses access_role. */
  requester_type?: RequesterType;
  access_scope: AccessScope;
  intended_use_case: string;
  location_ids: string[];
  namespaces: string[];
  expected_daily_volume: ExpectedDailyVolume;
  integrations: string[];
}

/**
 * Integrator request payload (Sandbox/Live). Sandbox must omit every scope
 * field; Live requires `access_scope` plus the matching list.
 */
export interface CreateIntegratorRequestPayload {
  api_platform: ApiPlatform;
  integrator_name: string;
  environment: KeyEnvironment;
  requester_type?: RequesterType;
  intended_use_case: string;
  access_scope?: AccessScope;
  namespaces?: string[];
  location_ids?: string[];
  location_names?: string[];
}

export interface CreateAccessRequestResponse {
  request: AccessRequest;
  message: string;
}

export interface ListAccessRequestsResponse {
  requests: AccessRequest[];
}

export interface RequestZapierIntegrationResponse {
  request: AccessRequest;
  message: string;
}

export interface RequestLiveCredentialsResponse {
  status: "none" | "pending" | "issued" | "failed";
  message: string;
}

/**
 * Contest payload. Omit optional fields to keep the current value.
 * When changing Live access_scope, send the matching list and clear the unused
 * list (`[]`) — omitted lists keep the prior value and mixed scope is rejected.
 * Do not send `environment` or `api_platform`.
 */
export interface ContestAccessRequestPayload {
  contestation_message: string;
  integrator_name?: string;
  intended_use_case?: string;
  access_scope?: AccessScope;
  location_ids?: string[];
  location_names?: string[];
  namespaces?: string[];
  expected_daily_volume?: ExpectedDailyVolume;
}

const ACCESS_REQUESTS_PATH = "/api/v1/access-requests";

export async function listLiveCatalog(
  query?: string,
  scope?: "namespaces" | "locations",
): Promise<LiveCatalogResponse> {
  const needle = query?.trim() ?? "";
  const params: Record<string, string> = {};
  if ([...needle].length >= 3) {
    params.q = needle;
  }
  if (scope) {
    params.scope = scope;
  }
  const { data } = await apiClient.get<LiveCatalogResponse>(
    `${ACCESS_REQUESTS_PATH}/live-catalog`,
    Object.keys(params).length > 0 ? { params } : undefined,
  );
  return data;
}

export async function listAccessRequests(): Promise<ListAccessRequestsResponse> {
  const { data } = await apiClient.get<ListAccessRequestsResponse>(
    ACCESS_REQUESTS_PATH,
  );
  return data;
}

export async function createAccessRequest(
  payload: CreateAccessRequestPayload,
): Promise<CreateAccessRequestResponse> {
  const { data } = await apiClient.post<CreateAccessRequestResponse>(
    ACCESS_REQUESTS_PATH,
    payload,
  );
  return data;
}

export async function createIntegratorRequest(
  payload: CreateIntegratorRequestPayload,
): Promise<CreateAccessRequestResponse> {
  const { data } = await apiClient.post<CreateAccessRequestResponse>(
    ACCESS_REQUESTS_PATH,
    payload,
  );
  return data;
}

export async function requestZapierIntegration(
  accessRequestId: string,
): Promise<RequestZapierIntegrationResponse> {
  const { data } = await apiClient.post<RequestZapierIntegrationResponse>(
    `${ACCESS_REQUESTS_PATH}/${accessRequestId}/integrations/zapier`,
  );
  return data;
}

/** Issues or resends live API credentials for an approved portal live scope. */
export async function requestLiveCredentials(
  accessRequestId: string,
): Promise<RequestLiveCredentialsResponse> {
  const { data } = await apiClient.post<RequestLiveCredentialsResponse>(
    `${ACCESS_REQUESTS_PATH}/${accessRequestId}/credentials`,
  );
  return data;
}

export async function contestAccessRequest(
  accessRequestId: string,
  payload: ContestAccessRequestPayload,
): Promise<AccessRequest> {
  const { data } = await apiClient.post<AccessRequest>(
    `${ACCESS_REQUESTS_PATH}/${accessRequestId}/contest`,
    payload,
  );
  return data;
}
